import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Mail, X, Check, RefreshCw, ArrowRight, ArrowLeft, Lock, Eye, EyeOff, AlertCircle, KeyRound, Shield, Clock } from 'lucide-react';
import { authService } from '../../services';
import { getDashboardTypeFromPort } from '../../utils/portUtils';
import GroupSvg from '../../svg/Group.svg';

// Validation helper functions

// Validate a single domain part (avoids backtracking in complex regex)
const validateDomainPart = (part) => {
  if (!part || part.length === 0 || part.length > 63) return false;
  
  // For single character, must be alphanumeric
  if (part.length === 1) {
    return /^[a-zA-Z0-9]$/.test(part);
  }
  
  // For multi-character: must start and end with alphanumeric, middle can have hyphens
  // This pattern is linear and safe from backtracking (no nested quantifiers)
  return /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/.test(part);
};

// Validate the domain part of the email
const validateDomain = (domain) => {
  if (!domain || domain.length === 0 || domain.length > 253) return false;
  if (!domain.includes('.')) return false;
  
  // Split domain into parts and validate each part separately to avoid regex backtracking
  const domainParts = domain.split('.');
  if (domainParts.length < 2) return false;
  
  // Check domain has valid TLD (at least 2 chars after last dot)
  const tld = domainParts[domainParts.length - 1];
  if (!tld || tld.length < 2) return false;
  
  // Validate each domain part separately
  for (const part of domainParts) {
    if (!validateDomainPart(part)) return false;
  }
  
  return true;
};

// Validate the local part of the email (before @)
const validateLocalPart = (localPart) => {
  if (!localPart || localPart.length === 0 || localPart.length > 64) return false;
  if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) return false;
  
  // Use simple, linear regex for local part validation (no nested quantifiers)
  const localPartRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
  return localPartRegex.test(localPart);
};

const isValidEmail = (email) => {
  if (!email) return false;
  // Limit input length to prevent ReDoS attacks (RFC 5321 specifies max 254 chars for email)
  if (email.length > 254) return false;
  
  // Use string operations first to avoid regex backtracking issues
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  
  const [localPart, domain] = parts;
  
  return validateLocalPart(localPart) && validateDomain(domain);
};

const isValidOtp = (otpString) => {
  return otpString && otpString.length === 6 && /^\d{6}$/.test(otpString);
};

const isValidPassword = (password) => {
  return password && password.length >= 8;
};

const isNumericInput = (value) => {
  return /^\d*$/.test(value);
};

const isPastedDataValid = (pastedData) => {
  return pastedData.length === 6 && /^\d{6}$/.test(pastedData);
};

const isOtpComplete = (otpArray) => {
  return otpArray.every(digit => digit !== '') && otpArray.length === 6;
};

// Step configuration
const STEP_CONFIG = {
  email: {
    title: 'Forgot Password?',
    subtitle: "No worries! We'll send you a verification code",
    icon: Mail,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    step: 1
  },
  otp: {
    title: 'Verify Your Identity',
    subtitle: 'Enter the 6-digit code sent to your email',
    icon: Shield,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    step: 2
  },
  newPassword: {
    title: 'Create New Password',
    subtitle: 'Choose a strong password to secure your account',
    icon: KeyRound,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    step: 3
  }
};

const getStepInfo = (step) => {
  return STEP_CONFIG[step] || {
    title: '',
    subtitle: '',
    icon: Mail,
    color: '',
    bgColor: '',
    step: 1
  };
};

// Helper function to get step progress indicator className
const getStepIndicatorClassName = (currentStep, stepNumber) => {
  if (currentStep === stepNumber) {
    return 'bg-[#1e3a8a] text-white border-[#1e3a8a]';
  }
  if (currentStep > stepNumber) {
    return 'bg-green-500 text-white border-green-500';
  }
  return 'bg-gray-100 text-gray-500 border-gray-300';
};

// Helper function to get OTP input field className
const getOtpInputClassName = (hasError, hasDigit) => {
  if (hasError) {
    return 'border-red-400 text-red-600';
  }
  if (hasDigit) {
    return 'border-[#1e3a8a] text-[#1e3a8a]';
  }
  return 'border-gray-300 hover:border-[#1e3a8a] text-gray-700';
};

// Helper function to get password requirement indicator className
const getPasswordRequirementClassName = (isMet) => {
  return isMet ? 'bg-green-500' : 'bg-gray-300';
};

// Helper function to check if passwords match
const doPasswordsMatch = (password, confirmPassword) => {
  return password === confirmPassword && password !== '';
};

// Helper function to get modal container width className
const getModalContainerWidth = (step) => {
  if (step === 'otp') {
    return 'max-w-sm sm:max-w-md lg:max-w-lg';
  }
  return 'max-w-md';
};

// Helper function to get card border radius className
const getCardBorderRadius = (step) => {
  if (step === 'otp') {
    return 'rounded-xl';
  }
  return 'rounded-2xl';
};

// Helper function to get card header padding className
const getCardHeaderPadding = (step) => {
  if (step === 'otp') {
    return 'pb-3';
  }
  return 'pt-6 pb-6';
};

// Helper function to get card content spacing className
const getCardContentSpacing = (step) => {
  if (step === 'otp') {
    return 'space-y-2 sm:space-y-3 px-4 sm:px-6 pb-4';
  }
  return 'space-y-4';
};

// Stable identifiers for OTP input fields
const OTP_INPUT_IDS = ['otp-input-0', 'otp-input-1', 'otp-input-2', 'otp-input-3', 'otp-input-4', 'otp-input-5'];

// Helper function to select forgot password API endpoint
const selectForgotPasswordAPI = async (isHospitalDashboard, isFleetDashboard, email) => {
  if (isHospitalDashboard) {
    return await authService.hospitalForgotPassword(email);
  }
  if (isFleetDashboard) {
    return await authService.fleetForgotPassword(email);
  }
  return await authService.forgotPassword(email);
};

// Helper function to extract OTP from general response
const extractOtpFromGeneralResponse = (result) => {
  if (result.data?.otp) {
    return result.data.otp;
  }
  if (result.data?.data?.otp && result.data?.data?.development) {
    return result.data.data.otp;
  }
  return null;
};

// Helper function to handle hospital dashboard response
const handleHospitalDashboardResponse = (result, formData, setDevOtp, setFormData, setStep, setOtp, setTimeLeft, setErrors) => {
  console.log('🏥 Hospital forgot password result:', result);
  
  const responseData = result.data;
  
  if (responseData.otp && responseData.development) {
    setDevOtp(responseData.otp);
    console.log('🏥 Development Mode - OTP:', responseData.otp);
    console.log('🏥 OTP expires in:', responseData.expiresIn, 'seconds');
  }
  
  setFormData(prev => ({ 
    ...prev, 
    contact: responseData.contact || formData.email,
    expiresIn: responseData.expiresIn
  }));
  
  setStep('otp');
  setOtp(['', '', '', '', '', '']);
  setTimeLeft(responseData.expiresIn || 300);
  setErrors({});
};

// Helper function to handle general forgot password response
const handleGeneralForgotPasswordResponse = (result, setDevOtp, setStep, setOtp, setTimeLeft, setErrors) => {
  console.log('🔐 Full result:', result);
  
  const otp = extractOtpFromGeneralResponse(result);
  if (otp) {
    setDevOtp(otp);
    console.log('🔐 Development Mode - OTP:', otp);
  }
  
  setStep('otp');
  setOtp(['', '', '', '', '', '']);
  setTimeLeft(300);
  setErrors({});
};

const ForgotPasswordModal = ({
  isOpen,
  onClose,
  step,
  setStep,
  formData,
  setFormData,
  loading,
  setLoading,
  errors,
  setErrors,
  success,
  setSuccess,
  dashboardType
}) => {
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [devOtp, setDevOtp] = React.useState(null);
  const [resetLink, setResetLink] = React.useState(null);
  const [otp, setOtp] = React.useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = React.useState(300); // 5 minutes in seconds
  const [isResending, setIsResending] = React.useState(false);
  const inputRefs = useRef([]);
  
  // Detect dashboard type
  const detectedDashboardType = dashboardType || getDashboardTypeFromPort();
  const isHospitalDashboard = detectedDashboardType === 'hospital';
  const isFleetDashboard = detectedDashboardType === 'fleet';
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  // Step 1: Send OTP/Reset Link to email
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    
    if (!isValidEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      setLoading(false);
      return;
    }
    
    try {
      const result = await selectForgotPasswordAPI(isHospitalDashboard, isFleetDashboard, formData.email);
      
      if (!result.success) {
        setLoading(false);
        return;
      }

      if (isHospitalDashboard && result.data) {
        handleHospitalDashboardResponse(
          result,
          formData,
          setDevOtp,
          setFormData,
          setStep,
          setOtp,
          setTimeLeft,
          setErrors
        );
      } else {
        handleGeneralForgotPasswordResponse(
          result,
          setDevOtp,
          setStep,
          setOtp,
          setTimeLeft,
          setErrors
        );
      }
    } catch (error) {
      console.error('[ForgotPassword] Send OTP/Reset Link error:', error);
      setErrors({ general: error.message || 'Failed to send reset link. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  // Timer countdown
  useEffect(() => {
    if (step === 'otp' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, step]);

  // Auto-focus first input when OTP step is shown
  useEffect(() => {
    if (step === 'otp' && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [step]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    // Only allow numbers
    if (!isNumericInput(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setErrors({ otp: '' });

    // Update formData.otp
    setFormData(prev => ({ ...prev, otp: newOtp.join('') }));

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (isOtpComplete(newOtp)) {
      handleVerifyOTPWithData(newOtp.join(''));
    }
  };

  const shouldMoveToPreviousInput = (key, currentValue, index) => {
    return key === 'Backspace' && !currentValue && index > 0;
  };

  const isPasteShortcut = (key, ctrlKey, metaKey) => {
    return key === 'v' && (ctrlKey || metaKey);
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (shouldMoveToPreviousInput(e.key, otp[index], index)) {
      inputRefs.current[index - 1]?.focus();
      return;
    }
    
    // Handle paste
    if (isPasteShortcut(e.key, e.ctrlKey, e.metaKey)) {
      e.preventDefault();
      handlePaste(e);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    
    if (!isPastedDataValid(pastedData)) {
      return;
    }

    const newOtp = pastedData.split('');
    setOtp(newOtp);
    setErrors({ otp: '' });
    setFormData(prev => ({ ...prev, otp: pastedData }));
    
    // Focus last input
    inputRefs.current[5]?.focus();
    
    // Auto-verify after a small delay
    setTimeout(() => {
      handleVerifyOTPWithData(pastedData);
    }, 100);
  };

  // Step 2: Verify OTP
  const handleVerifyOTPWithData = async (otpString) => {
    if (!isValidOtp(otpString)) {
      setErrors({ otp: 'Please enter a complete 6-digit OTP' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Verify OTP with backend based on dashboard type
      let result;
      if (isFleetDashboard) {
        result = await authService.fleetVerifyResetOtp(formData.email, otpString);
      } else {
        // Admin dashboard - use general OTP verification
        result = await authService.verifyOtp(formData.email, otpString, 'forgot_password');
      }

      if (result.success) {
        console.log('✅ OTP verified successfully');
        setStep('newPassword');
        setErrors({});
      } else {
        setErrors({ otp: 'Invalid OTP. Please try again.' });
      }
    } catch (error) {
      console.error('[ForgotPassword] Verify OTP error:', error);
      setErrors({ otp: error.message || 'Invalid OTP. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    if (e) e.preventDefault();
    const otpString = otp.join('');
    if (!isValidOtp(otpString)) {
      setErrors({ otp: 'Please enter a complete 6-digit OTP' });
      return;
    }
    await handleVerifyOTPWithData(otpString);
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    setErrors({});
    setOtp(['', '', '', '', '', '']);
    setTimeLeft(300);
    
    try {
      await handleSendOTP({ preventDefault: () => {} });
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error('Failed to resend OTP:', error);
      setErrors({ general: error.message || 'Failed to resend OTP. Please try again.' });
    } finally {
      setIsResending(false);
    }
  };
  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Validate passwords - early returns
    if (!isValidPassword(formData.newPassword)) {
      setErrors({ newPassword: 'Password must be at least 8 characters long' });
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      setLoading(false);
      return;
    }
    try {
      const result = await authService.resetPassword(
        formData.email,
        formData.otp,
        formData.newPassword
      );
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error('[ForgotPassword] Reset password error:', error);
      setErrors({ general: error.message || 'Failed to reset password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  // Handle close and reset
  const handleClose = () => {
    setStep('email');
    setFormData({ email: '', otp: '', newPassword: '', confirmPassword: '' });
    setErrors({});
    setSuccess(false);
    setLoading(false);
    setResetLink(null);
    setDevOtp(null);
    onClose();
  };
  const stepInfo = getStepInfo(step);
  const StepIcon = stepInfo.icon;

  // Render step progress indicator
  const renderStepProgress = (marginTopClass = 'mt-3') => {
    return (
      <div className={`flex items-center justify-center space-x-2 ${marginTopClass}`}>
        {[1, 2, 3].map((num) => (
          <div key={num} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs transition-all duration-300 border ${getStepIndicatorClassName(stepInfo.step, num)}`}
            >
              {stepInfo.step > num ? <Check className="w-4 h-4" /> : num}
            </div>
            {num < 3 && (
              <div
                className={`w-8 h-0.5 mx-1 rounded transition-all duration-300 ${
                  stepInfo.step > num ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render email step form
  const renderEmailStep = () => {
    return (
      <form onSubmit={handleSendOTP} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="forgot-email" className="block text-sm font-semibold text-gray-900">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="forgot-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`pl-12 sm:pl-14 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
              placeholder="Enter your registered email or phone number"
              disabled={loading}
              autoFocus
            />
          </div>
          {errors.email && (
            <p className="text-sm text-red-600 flex items-center space-x-1">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.email}</span>
            </p>
          )}
        </div>
        <Button
          type="submit"
          className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white font-medium py-3 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:opacity-50"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Sending Verification Code...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <span>Send Verification Code</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          )}
        </Button>
      </form>
    );
  };

  // Render OTP step form
  const renderOtpStep = () => {
    return (
      <form onSubmit={handleVerifyOTP} className="space-y-2 sm:space-y-3">
        {devOtp && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1.5">
              <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <p className="text-xs font-semibold text-blue-900">Development Mode</p>
            </div>
            <p className="text-xs text-blue-700 mb-1.5">Your OTP code is:</p>
            <p className="text-xl font-bold text-blue-900 text-center tracking-widest bg-white rounded p-2 border border-blue-300">
              {devOtp}
            </p>
            <p className="text-xs text-blue-600 mt-1.5 text-center">
              This is displayed for testing purposes only
            </p>
          </div>
        )}

        {errors.otp && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-center space-x-2">
            <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0" />
            <p className="text-xs text-red-600">{errors.otp}</p>
          </div>
        )}

        <div className="space-y-2">
          <fieldset className="flex justify-center space-x-2 sm:space-x-3 border-0 p-0 m-0">
            <legend className="text-xs sm:text-sm font-medium text-gray-700 text-center block mb-2 w-full">
              Enter verification code
            </legend>
            {otp.map((digit, index) => (
              <input
                key={OTP_INPUT_IDS[index]}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                aria-label={`OTP digit ${index + 1} of 6`}
                className={`w-8 h-10 sm:w-10 sm:h-12 text-center text-lg sm:text-xl font-bold bg-transparent border-0 border-b-2 focus:outline-none transition-all duration-200 ${getOtpInputClassName(!!errors.otp, !!digit)}`}
                disabled={loading}
              />
            ))}
          </fieldset>
        </div>

        <div className="text-center">
          {timeLeft > 0 ? (
            <div className="flex items-center justify-center space-x-1 text-xs text-gray-600">
              <Clock className="w-3 h-3" />
              <span>Code expires in {formatTime(timeLeft)}</span>
            </div>
          ) : (
            <p className="text-xs text-red-600">Code has expired</p>
          )}
        </div>

        <Button
          type="submit"
          onClick={handleVerifyOTP}
          disabled={loading || otp.some(digit => digit === '') || timeLeft === 0}
          className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white font-medium py-2 sm:py-2.5 text-sm rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:opacity-50"
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Verifying...</span>
            </div>
          ) : (
            'Verify Email'
          )}
        </Button>

        <div className="text-center">
          <p className="text-xs text-gray-600 mb-1.5">
            Didn't receive the code?
          </p>
          <Button
            onClick={handleResendOtp}
            disabled={isResending}
            variant="outline"
            className="w-full border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-50 py-2 text-xs sm:text-sm"
          >
            {isResending ? (
              <div className="flex items-center space-x-2">
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Resending...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <RefreshCw className="w-3 h-3" />
                <span>Resend Code</span>
              </div>
            )}
          </Button>
        </div>

        <div className="text-center pt-1.5 sm:pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setStep('email')}
            className="flex items-center space-x-2 text-xs text-gray-600 hover:text-[#1e3a8a] transition-colors mx-auto"
            disabled={loading}
          >
            <ArrowLeft className="w-3 h-3" />
            <span>Back to Email</span>
          </button>
        </div>
      </form>
    );
  };

  // Render new password step form
  const renderNewPasswordStep = () => {
    return (
      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="forgot-newPassword" className="block text-sm font-semibold text-gray-900">
            New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="forgot-newPassword"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleInputChange}
              className={`pl-12 sm:pl-14 pr-12 sm:pr-14 ${errors.newPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
              placeholder="Enter new password (min. 8 characters)"
              disabled={loading}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
              disabled={loading}
              aria-label={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-sm text-red-600 flex items-center space-x-1">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.newPassword}</span>
            </p>
          )}
        </div>
        <div className="space-y-2">
          <label htmlFor="forgot-confirmPassword" className="block text-sm font-semibold text-gray-900">
            Confirm New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="forgot-confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`pl-12 sm:pl-14 pr-12 sm:pr-14 ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
              placeholder="Re-enter your new password"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
              disabled={loading}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-red-600 flex items-center space-x-1">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.confirmPassword}</span>
            </p>
          )}
        </div>
        
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <p className="text-xs font-semibold text-gray-800 mb-2">Password Requirements:</p>
          <ul className="text-xs text-gray-700 space-y-1">
            <li className="flex items-center space-x-2">
              <div className={`w-1.5 h-1.5 rounded-full ${getPasswordRequirementClassName((formData.newPassword || '').length >= 8)}`}></div>
              <span>At least 8 characters long</span>
            </li>
            <li className="flex items-center space-x-2">
              <div className={`w-1.5 h-1.5 rounded-full ${getPasswordRequirementClassName(doPasswordsMatch(formData.newPassword, formData.confirmPassword))}`}></div>
              <span>Passwords match</span>
            </li>
          </ul>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep('otp')}
            className="flex-1"
            disabled={loading}
          >
            Back
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white font-medium py-3 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
            ) : (
              <div className="flex items-center space-x-2">
                <span>Reset Password</span>
                <Check className="w-4 h-4" />
              </div>
            )}
          </Button>
        </div>
      </form>
    );
  };

  // Render card header based on step
  const renderCardHeader = () => {
    if (step === 'otp') {
      return (
        <>
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3" style={{backgroundColor: '#1e3a8a', border: '2px solid white'}}>
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <CardTitle className="text-lg sm:text-xl font-bold text-gray-900">
            Verify Your Email
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm text-gray-600 px-2 sm:px-0">
            We've sent a 6-digit verification code to
          </CardDescription>
          <p className="text-xs font-medium text-[#1e3a8a] break-all px-2 sm:px-0">{formData.email}</p>
          {renderStepProgress()}
        </>
      );
    }
    
    return (
      <>
        <div className="flex justify-center mb-4">
          <img src={GroupSvg} alt="Smart Ambulance Logo" className="h-10 w-auto" />
        </div>
        <CardTitle className="text-2xl font-bold text-gray-900">
          {stepInfo.title}
        </CardTitle>
        <CardDescription className="text-gray-600 mt-2">
          {stepInfo.subtitle}
        </CardDescription>
        {renderStepProgress('mt-4')}
      </>
    );
  };

  // Render step content based on current step
  const renderStepContent = () => {
    if (step === 'email') return renderEmailStep();
    if (step === 'otp') return renderOtpStep();
    if (step === 'newPassword') return renderNewPasswordStep();
    return null;
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-300" style={{
      background: 'linear-gradient(135deg, rgba(248, 249, 250, 0.6) 0%, rgba(227, 242, 253, 0.6) 100%)'
    }}>
      <div className={`w-full ${getModalContainerWidth(step)} mx-auto`}>
        <Card className={`shadow-xl ${getCardBorderRadius(step)} bg-white/80 backdrop-blur-sm relative`}>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          
          <CardHeader className={`space-y-2 text-center ${getCardHeaderPadding(step)}`}>
            {renderCardHeader()}
          </CardHeader>

          <CardContent className={`${getCardContentSpacing(step)} !pt-0`}>
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  <p className="text-sm text-green-600">Password Reset Successful! You can now login with your new password.</p>
                </div>
              </div>
            )}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <p className="text-sm text-red-600">{errors.general}</p>
                </div>
              </div>
            )}
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

ForgotPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  step: PropTypes.oneOf(['email', 'otp', 'newPassword']).isRequired,
  setStep: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    email: PropTypes.string.isRequired,
    otp: PropTypes.string.isRequired,
    newPassword: PropTypes.string.isRequired,
    confirmPassword: PropTypes.string.isRequired,
    contact: PropTypes.string,
    resetLink: PropTypes.string,
    expiresIn: PropTypes.number
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    general: PropTypes.string,
    email: PropTypes.string,
    otp: PropTypes.string,
    newPassword: PropTypes.string,
    confirmPassword: PropTypes.string
  }).isRequired,
  setErrors: PropTypes.func.isRequired,
  success: PropTypes.bool.isRequired,
  setSuccess: PropTypes.func.isRequired,
  dashboardType: PropTypes.string
};

export default ForgotPasswordModal;
